import React, { useEffect, useState } from 'react';
import { PaymentLogo, LABELS } from '../../components/common/PaymentIcon';
import api from '../../utils/api';

const STATUS_STYLES = {
  pending:  { bg: 'rgba(245,196,0,0.12)',  color: '#f5c400',  label: '⏳ Pending'  },
  verified: { bg: 'rgba(16,185,129,0.12)', color: '#10b981', label: '✅ Verified' },
  rejected: { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444',  label: '❌ Rejected' },
};

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [imgModal, setImgModal] = useState(null);
  const [searchUser, setSearchUser] = useState('');
  const [searchDate, setSearchDate] = useState('');

  const load = () => api.get('/deposits/admin').then(({ data }) => setDeposits(data.deposits || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const counts = {
    all:      deposits.length,
    pending:  deposits.filter(d => d.status === 'pending').length,
    verified: deposits.filter(d => d.status === 'verified').length,
    rejected: deposits.filter(d => d.status === 'rejected').length,
  };

  const filtered = deposits.filter(d => {
    const matchStatus = filter === 'all' || d.status === filter;
    const matchUser = !searchUser || d.user?.username?.toLowerCase().includes(searchUser.toLowerCase()) || d.user?.email?.toLowerCase().includes(searchUser.toLowerCase());
    const matchDate = !searchDate || new Date(d.createdAt).toLocaleDateString().includes(searchDate) || d.createdAt?.slice(0,10) === searchDate;
    return matchStatus && matchUser && matchDate;
  });

  const handleAction = async (id, status) => {
    setUpdating(true);
    try {
      await api.put(`/deposits/${id}/status`, { status, adminNote });
      await load();
      setSelected(null); setAdminNote('');
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    finally { setUpdating(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>DEPOSITS</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Review payment screenshots and verify user deposits</p>
        </div>
        {counts.pending > 0 && (
          <div style={{ background: 'rgba(245,196,0,0.12)', border: '1px solid #f5c400', borderRadius: 10, padding: '8px 16px', color: '#f5c400', fontWeight: 700, fontSize: 14, animation: 'pulse 2s infinite' }}>
            🔔 {counts.pending} need review
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {Object.entries(counts).map(([key, count]) => (
          <button key={key} onClick={() => setFilter(key)} className={`btn btn-sm ${filter === key ? 'btn-primary' : 'btn-secondary'}`} style={{ textTransform: 'capitalize' }}>
            {key} ({count})
          </button>
        ))}
      </div>

      {/* Search filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <input placeholder="Search by username or email..." value={searchUser} onChange={e => setSearchUser(e.target.value)} style={{ maxWidth:260 }} />
        <input type="date" value={searchDate} onChange={e => setSearchDate(e.target.value)} style={{ maxWidth:180 }} title="Filter by date" />
        {(searchUser || searchDate) && <button className="btn btn-secondary btn-sm" onClick={() => { setSearchUser(''); setSearchDate(''); }}>✕ Clear</button>}
        <span style={{ color:'var(--text-muted)', fontSize:13, alignSelf:'center' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
        <>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💰</div>
              <p>No {filter} deposits</p>
            </div>
          )}

          {/* Table view */}
          {filtered.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                      {['Date','User','Game / Username','Amount','Payment','Tag','Screenshot','Status','Action'].map(h => (
                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(d => (
                      <tr key={d._id} style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(d.createdAt).toLocaleDateString()}<br/>
                          <span style={{ fontSize: 11 }}>{new Date(d.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{d.user?.username}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.user?.email}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>👤 {d.fullName}</div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#f5c400' }}>{d.platform?.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>@{d.gameUsername}</div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 18, color: '#10b981' }}>${d.amount}</div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <PaymentLogo type={d.paymentMethod?.type} size={24} />
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{d.paymentMethod?.name || LABELS[d.paymentMethod?.type]}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {d.paymentMethod?.tag
                            ? <span style={{ color: '#f5c400', fontWeight: 700, fontSize: 13 }}>{d.paymentMethod.tag}</span>
                            : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {d.screenshot
                            ? <img src={d.screenshot} alt="proof" onClick={() => setImgModal(d.screenshot)} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', border: '1px solid var(--border)' }}
                                onMouseEnter={e => e.target.style.borderColor = '#f5c400'}
                                onMouseLeave={e => e.target.style.borderColor = 'var(--border)'} />
                            : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>None</span>}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: STATUS_STYLES[d.status]?.bg, color: STATUS_STYLES[d.status]?.color, whiteSpace: 'nowrap' }}>{STATUS_STYLES[d.status]?.label}</span>
                          {d.notificationSent && <div style={{ fontSize: 10, color: '#10b981', marginTop: 3 }}>💬 notified</div>}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {d.status === 'pending'
                            ? <button className="btn btn-primary btn-sm" onClick={() => { setSelected(d); setAdminNote(''); }}>Review</button>
                            : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.adminNote ? `📝 ${d.adminNote.slice(0, 20)}...` : '—'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20 }}>Review Deposit</h2>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                {[
                  ['User', selected.user?.username],
                  ['Date', new Date(selected.createdAt).toLocaleString()],
                  ['Game', selected.platform?.name],
                  ['Game Username', selected.gameUsername],
                  ['Full Name', selected.fullName],
                  ['Amount', `$${selected.amount}`],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: 'var(--bg-card)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#f5c400' }}>{v}</div>
                  </div>
                ))}
                {selected.paymentMethod && (
                  <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, gridColumn: '1 / -1' }}>
                    <PaymentLogo type={selected.paymentMethod.type} size={28} />
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 1 }}>PAYMENT METHOD</div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {selected.paymentMethod.name || LABELS[selected.paymentMethod.type]}
                        {selected.paymentMethod.tag && <span style={{ color: '#f5c400', marginLeft: 8 }}>— {selected.paymentMethod.tag}</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {selected.screenshot && (
                <img src={selected.screenshot} alt="proof" style={{ width: '100%', borderRadius: 10, maxHeight: 220, objectFit: 'contain', background: 'var(--bg-card)', cursor: 'zoom-in', border: '1px solid var(--border)' }} onClick={() => setImgModal(selected.screenshot)} />
              )}
            </div>
            <div className="form-group">
              <label>Message to User <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(sent via Live Chat)</span></label>
              <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} rows={2} placeholder="e.g. Your deposit has been loaded! Enjoy playing." />
            </div>
            <div style={{ background: 'rgba(245,196,0,0.06)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
              💬 A message is automatically sent to the user via Live Chat.
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => handleAction(selected._id, 'verified')} disabled={updating}
                style={{ flex: 1, padding: '14px', background: 'rgba(16,185,129,0.15)', border: '2px solid #10b981', borderRadius: 10, color: '#10b981', fontWeight: 700, cursor: 'pointer', fontSize: 15, fontFamily: 'var(--font-body)' }}>
                {updating ? '⏳...' : '✅ Verify & Load'}
              </button>
              <button onClick={() => handleAction(selected._id, 'rejected')} disabled={updating}
                style={{ flex: 1, padding: '14px', background: 'rgba(239,68,68,0.1)', border: '2px solid #ef4444', borderRadius: 10, color: '#ef4444', fontWeight: 700, cursor: 'pointer', fontSize: 15, fontFamily: 'var(--font-body)' }}>
                {updating ? '⏳...' : '❌ Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full screenshot modal */}
      {imgModal && (
        <div className="modal-overlay" onClick={() => setImgModal(null)} style={{ background: 'rgba(0,0,0,0.95)' }}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button onClick={() => setImgModal(null)} style={{ position: 'absolute', top: -44, right: 0, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            <img src={imgModal} alt="full screenshot" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, objectFit: 'contain' }} />
          </div>
        </div>
      )}
    </div>
  );
}
